<template>
  <DsPageContent>
    <DsSection :with-space="false">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsScreenWrapper
            v-if="content.headingMedia"
            :ready="hydrateReady"
          >
            <DsHeaderWithDivider
              :image="content.headingMedia"
              :screen="screen"
              breakpoint="medium"
              theme="light"
            />
          </DsScreenWrapper>
        </div>
      </div>
    </DsSection>
    <div class="ds-container ds-margin-top-48 ds-md-margin-top-0">
      <CareersFilters
        v-if="filters"
        ref="careersFilters"
        :jobs-data="jobsData"
        :results="filteredJobs.length"
      />
    </div>
    <div class="ds-container ds-margin-y-48 ds-md-margin-y-96">
      <div class="ds-grid">
        <div class="ds-grid__col">
          <CareersVacanciesList
            :items="filteredJobs"
            :with-limit="false"
            :sort-by-latest="false"
            @filter="handleFilter"
          />
        </div>
      </div>
    </div>
  </DsPageContent>
</template>
<script setup>
import { DsPageContent, DsSection, DsScreenWrapper, DsHeaderWithDivider } from 'awds'

import { isEqual } from 'lodash-es'
import { queryPageCareersVacancies } from '../../data-layer/queries'

const route = useRoute()
const router = useRouter()
const emit = defineEmits(['search', 'selected'])
const { screen } = useScreen()
const { locale } = useI18n()
const { useAsyncDataWithQuery, onBeforeAdyenData, onAfterAdyenData, content } = useCareersPageData()
const { jobsData, getGreenhouseJobsData, fetchAllGreenhouseData, getFilterForFilterName, JobFilter, jobs } =
  useGreenHouse()
const { setFilter, filters, query } = useFilters(emit)
const { hydrateReady } = useHydrateHook()

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PageCareersVacancies' })

const { data } = await useAsyncData('careers-vacancies-data-' + locale.value, async () => {
  // Cities, FormsLabels
  const page = await useAsyncDataWithQuery(queryPageCareersVacancies, {})

  return {
    pageData: page?.pageData
  }
})

await onAfterAdyenData(data.value)

onMounted(async () => {
  // If on cache, this will not trigger a call
  await getGreenhouseJobsData({ sortByLatest: false, withLimit: false })

  const parsedFilterParams = parseRouteParams(route.query)
  filters.value = parsedFilterParams || []

  // We still fetch all the jobs in the background
  setTimeout(() => {
    // If we do have jobs, we still fetch them, but in a non-blocking way
    fetchAllGreenhouseData({ sortByLatest: false, withLimit: false })
  }, 1000)
})

const filteredJobs = computed(() => {
  if (!filters.value || (filters.value.length === 0 && !query.value)) {
    return jobs.value || []
  }

  const locationFilter = filters.value.find((f) => f.name === 'location')
  const teamFilter = filters.value.find((f) => f.name === 'team')
  return jobs.value.filter((job) => {
    let isValid = true
    if (query.value) {
      isValid = isValid && job.name.toLowerCase().includes(query.value.toLowerCase())
    }

    if (locationFilter) {
      isValid = isValid && job.offices.some((o) => locationFilter.value.includes(o.id))
    }

    if (teamFilter) {
      isValid = isValid && job.departments.some((d) => teamFilter.value.includes(d.id))
    }

    return isValid
  })
})

const parseRouteParams = (query) => {
  const routeParams = Object.values(JobFilter)
    .map((filter) => {
      const queryValues = query?.[filter.query]
      if (!queryValues) {
        return null
      }
      const parsedValues = Array.isArray(queryValues) ? queryValues : [queryValues]
      const idValues =
        parsedValues
          .map(
            (val) => jobsData.value?.[filter.ghParam]?.find((job) => job.name.toLowerCase() === val.toLowerCase())?.id
          )
          .filter(Boolean) || []

      if (idValues.length === 0) {
        return null
      }

      return {
        ...filter,
        value: idValues
      }
    })
    .filter(Boolean)

  return routeParams
}

const handleFilter = (filter) => {
  if (filters.value.some((f) => f.name === filter.name)) {
    return
  }
  setFilter(filter.name, filter.value)
  updateFiltersURLParams()
}

watchEffect(() => {
  if (filters.value.length > 0) {
    updateFiltersURLParams(filters.value)
  }
})

function updateFiltersURLParams() {
  const query = {}
  filters.value?.forEach((item) => {
    const filter = getFilterForFilterName(item.name)
    const stringValues = item.value
      .map((val) => {
        return jobsData.value[filter.ghParam].find((elem) => val === elem.id)?.name
      })
      .filter(Boolean)

    // To match vue-routing structure
    query[item.name] = stringValues.length === 1 ? stringValues[0] : stringValues
  })

  // If same query params, do not refresh
  if (isEqual(query, route.query)) {
    return
  }

  // Refresh query params
  router.replace({ name: route.name, query })
}
</script>
